import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #pendingTimer = null;
  /** @type {MutationObserver | null} */
  #pendingObserver = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #pendingHardTimer = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    document.addEventListener('click', this.#handleNavigationClick, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    document.removeEventListener('click', this.#handleNavigationClick, true);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const isCartPage = document.querySelector('.cart-page') !== null;
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen && !isCartPage;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Opening only when the cart already had something in it meant the very
    // first add — the one with the longest wait — got no drawer until the
    // response came back. The waiting state is what makes opening on an empty
    // cart safe, so the two go together.
    const wasEmpty = this.#isCartEmpty();
    if (shouldAutoOpen && !sourceModal) {
      this.#startPending();
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (detail?.didError) {
          this.#endPending();
          if (wasEmpty) this.#themeDrawer?.close();
          settle();
          return;
        }

        if (!shouldAutoOpen) {
          this.#endPending();
          settle();
          return;
        }

        this.#endPendingWhenSettled();

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
        this.#endPending();
        // An add that failed against an empty cart would leave the drawer open
        // on "your cart is empty", which reads as though the tap emptied it.
        // The error itself stays on the product page, where the button is.
        if (wasEmpty) this.#themeDrawer?.close();
      });
  };

  #handleNavigationClick = (event) => {
    if (!this.#themeDrawer?.isOpen) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[href]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('#') || href.startsWith('javascript:')) return;
    if (
      link.closest('#cart-drawer') ||
      link.closest('dialog') ||
      link.closest('[popover]') ||
      link.closest('search-button') ||
      link.closest('search-modal') ||
      link.closest('predictive-search')
    ) return;
    this.#themeDrawer.close();
  };

  /**
   * Shows that an add is under way.
   *
   * The drawer opens on the tap now, not on the response, so for a second or
   * more it would otherwise sit there looking like nothing happened — and if
   * the cart was empty it would answer the tap with "your cart is empty". The
   * banner says what is happening and the attribute hides that message.
   *
   * No placeholder line item: a fake row has to be reconciled against whatever
   * the server actually returns, and gets it wrong on every error path.
   */
  #startPending() {
    const dialog = this.#dialog;
    const inner = dialog?.querySelector('.cart-drawer__inner');
    if (!dialog || !inner) return;

    dialog.setAttribute('jv-pending', 'true');

    // Last resort. The normal teardown hangs off the promise, so a request that
    // never settles — a socket iOS holds open after airplane mode — would leave
    // the banner up for good. Longer than the configurator's own 25s abort, so
    // that path still gets to run first and report properly.
    if (this.#pendingHardTimer) clearTimeout(this.#pendingHardTimer);
    this.#pendingHardTimer = setTimeout(() => this.#endPending(), 30000);

    if (!inner.querySelector('[data-jv-pending]')) {
      const banner = document.createElement('div');
      banner.className = 'cart-drawer__pending';
      banner.setAttribute('data-jv-pending', '');
      banner.setAttribute('role', 'status');
      banner.textContent = 'Adding your strap…';
      // Below the header, above the items. Prepending pushed the title and the
      // close button down and snapped them back when the banner went, which
      // read as the drawer jumping. The empty state renders its own header
      // too, so this lands in the same place either way.
      const header = inner.querySelector('.theme-drawer__header');
      if (header) header.after(banner);
      else inner.prepend(banner);
    }
  }

  /**
   * Clears the waiting state.
   *
   * Timing it off the response alone is not enough: the morph that replaces the
   * drawer contents runs in cart-items-component, sometimes inside a view
   * transition, so it lands after the promise and on its own schedule. Watch
   * for the contents actually changing, and keep a timeout so a morph that
   * never arrives cannot strand the banner.
   */
  #endPendingWhenSettled() {
    const dialog = this.#dialog;
    if (!dialog || !dialog.hasAttribute('jv-pending')) return;

    const finish = () => this.#endPending();

    this.#pendingObserver?.disconnect();
    const inner = dialog.querySelector('.cart-drawer__inner');
    if (inner) {
      this.#pendingObserver = new MutationObserver((records) => {
        // The banner is our own; its removal is not the morph landing.
        const meaningful = records.some((record) =>
          [...record.addedNodes, ...record.removedNodes].some(
            (node) => !(node instanceof Element) || !node.hasAttribute('data-jv-pending')
          )
        );
        if (meaningful) finish();
      });
      this.#pendingObserver.observe(inner, { childList: true, subtree: true });
    }

    if (this.#pendingTimer) clearTimeout(this.#pendingTimer);
    this.#pendingTimer = setTimeout(finish, 2000);
  }

  /** Tears the waiting state down. Safe to call more than once. */
  #endPending() {
    if (this.#pendingTimer) {
      clearTimeout(this.#pendingTimer);
      this.#pendingTimer = null;
    }
    // Cleared here so a normal finish cannot take down the next add's banner.
    if (this.#pendingHardTimer) {
      clearTimeout(this.#pendingHardTimer);
      this.#pendingHardTimer = null;
    }
    this.#pendingObserver?.disconnect();
    this.#pendingObserver = null;

    const dialog = this.#dialog;
    dialog?.removeAttribute('jv-pending');
    dialog?.querySelector('[data-jv-pending]')?.remove();
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
